import { useMemo } from 'react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { getSportAccent } from '../../config/sportAccents';
import type { SportRecommendation, IntelligenceSignal, ExposureReport } from '../../types';
import { TeamLogo } from '../common/TeamLogo';

// ── Priority config ───────────────────────────────────────────────────────────

type Priority = 'HIGH' | 'MED' | 'LOW';

const PRIORITY_CFG: Record<Priority, { cls: string; dotCls: string; textCls: string }> = {
  HIGH: { cls: 'bg-loss/20 text-loss border-loss/40',   dotCls: 'bg-loss',  textCls: 'text-loss'  },
  MED:  { cls: 'bg-warn/20 text-warn border-warn/40',   dotCls: 'bg-warn',  textCls: 'text-warn'  },
  LOW:  { cls: 'bg-info/20 text-info border-info/40',   dotCls: 'bg-info',  textCls: 'text-info'  },
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface DecisionItem {
  id: string;
  priority: Priority;
  leagueId?: string;
  actionText: string;
  rationale: string;
  countdown?: number | null;
  edgeDecay?: { decayPct: number; remainingEdge: number } | null;
  rec?: SportRecommendation;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function minutesUntil(iso?: string): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  return Math.round((t - Date.now()) / 60_000);
}

function formatCountdown(mins: number): string {
  if (mins <= 0) return 'Starting';
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function computeDecay(scheduledDate: string, edge: number) {
  const minsUntil = minutesUntil(scheduledDate) ?? 240;
  const decayPct = minsUntil >= 240 ? 0 : Math.max(0, 1 - minsUntil / 240);
  const remainingEdge = edge * (1 - decayPct * 0.4);
  return { decayPct, remainingEdge };
}

// ── Derive decision items ─────────────────────────────────────────────────────

function deriveDecisions(
  picks: SportRecommendation[],
  signals: IntelligenceSignal[],
  exposure: ExposureReport | null,
): DecisionItem[] {
  const items: DecisionItem[] = [];
  const usedPickIds = new Set<string>();

  // HIGH — BET_NOW picks
  for (const p of picks) {
    if (p.timing !== 'BET_NOW') continue;
    if (p.decisionStatus === 'NO_BET' || p.decisionStatus === 'INVALID') continue;
    usedPickIds.add(p.id);
    items.push({
      id: `bet-now-${p.id}`,
      priority: 'HIGH',
      leagueId: p.leagueId,
      actionText: `Place ${p.awayTeamName} @ ${p.homeTeamName} — ${p.market}`,
      rationale: `${p.decisionStatus === 'STRONG_BET' ? 'Strong' : 'Qualified'} bet · ${p.edge.toFixed(1)}% edge`,
      countdown: minutesUntil(p.scheduledDate),
      edgeDecay: computeDecay(p.scheduledDate, p.edge),
      rec: p,
    });
  }

  // HIGH — WAIT picks closing fast (< 60 min, confidence ≥ 0.65)
  for (const p of picks) {
    if (usedPickIds.has(p.id)) continue;
    if (p.timing !== 'WAIT') continue;
    if (p.confidence < 0.65) continue;
    const countdown = minutesUntil(p.scheduledDate);
    if (countdown === null || countdown >= 60) continue;
    usedPickIds.add(p.id);
    items.push({
      id: `closing-${p.id}`,
      priority: 'HIGH',
      leagueId: p.leagueId,
      actionText: `Window closing — ${p.awayTeamName} @ ${p.homeTeamName}`,
      rationale: `${p.market} · CLV window < 1h · ${Math.round(p.confidence * 100)}% confidence`,
      countdown,
      edgeDecay: computeDecay(p.scheduledDate, p.edge),
      rec: p,
    });
  }

  // HIGH — exposure violations
  for (const v of (exposure?.violations ?? []).slice(0, 3)) {
    items.push({
      id: `violation-${v}`,
      priority: 'HIGH',
      actionText: v,
      rationale: 'Risk limit exceeded — review exposure',
    });
  }

  // HIGH — high severity signals
  for (const s of signals.filter((s) => s.isActive && s.severity === 'high').slice(0, 2)) {
    items.push({
      id: `signal-high-${s.id}`,
      priority: 'HIGH',
      leagueId: s.leagueId,
      actionText: s.title,
      rationale: s.description?.slice(0, 80) ?? '',
    });
  }

  // MED — WAIT picks closing < 120 min, confidence ≥ 0.55
  for (const p of picks) {
    if (usedPickIds.has(p.id)) continue;
    if (p.timing !== 'WAIT') continue;
    if (p.confidence < 0.55) continue;
    const countdown = minutesUntil(p.scheduledDate);
    if (countdown === null || countdown >= 120) continue;
    usedPickIds.add(p.id);
    items.push({
      id: `wait-${p.id}`,
      priority: 'MED',
      leagueId: p.leagueId,
      actionText: `Monitor — ${p.awayTeamName} @ ${p.homeTeamName} in ${formatCountdown(countdown)}`,
      rationale: `${p.market} · ${p.edge.toFixed(1)}% edge · WAIT timing`,
      countdown,
      rec: p,
    });
  }

  // MED — warn signals
  for (const s of signals.filter((s) => s.isActive && s.severity === 'warn').slice(0, 3)) {
    items.push({
      id: `signal-warn-${s.id}`,
      priority: 'MED',
      leagueId: s.leagueId,
      actionText: s.title,
      rationale: `${s.leagueId?.toUpperCase() ?? ''} · ${s.type.replace(/_/g, ' ')}`,
    });
  }

  // LOW — MONITOR strong/qualified picks
  for (const p of picks) {
    if (usedPickIds.has(p.id)) continue;
    if (p.timing !== 'MONITOR') continue;
    if (p.decisionStatus !== 'STRONG_BET' && p.decisionStatus !== 'QUALIFIED_BET') continue;
    items.push({
      id: `monitor-${p.id}`,
      priority: 'LOW',
      leagueId: p.leagueId,
      actionText: `Watch ${p.awayTeamName} @ ${p.homeTeamName} — ${p.market}`,
      rationale: `${p.decisionStatus === 'STRONG_BET' ? 'Strong' : 'Qualified'} · Timing not yet right`,
      rec: p,
    });
  }

  // LOW — info signals
  for (const s of signals.filter((s) => s.isActive && s.severity === 'info').slice(0, 3)) {
    items.push({
      id: `signal-info-${s.id}`,
      priority: 'LOW',
      leagueId: s.leagueId,
      actionText: s.title,
      rationale: s.type.replace(/_/g, ' '),
    });
  }

  return items;
}

// ── Edge decay bar ────────────────────────────────────────────────────────────

function DecayBar({ decayPct, remainingEdge }: { decayPct: number; remainingEdge: number }) {
  const colorCls = decayPct < 0.3 ? 'bg-win' : decayPct < 0.65 ? 'bg-warn' : 'bg-loss';
  const textCls  = decayPct < 0.3 ? 'text-win' : decayPct < 0.65 ? 'text-warn' : 'text-loss';
  return (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      <div className="w-10 h-1 bg-surface-3 rounded-full overflow-hidden">
        <motion.div
          className={clsx('h-full rounded-full', colorCls)}
          initial={{ width: '100%' }}
          animate={{ width: `${Math.max((1 - decayPct) * 100, 2)}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>
      <span className={clsx('text-[9px] font-mono font-bold tabular-nums', textCls)}>
        {remainingEdge.toFixed(1)}%
      </span>
    </div>
  );
}

// ── Decision row ──────────────────────────────────────────────────────────────

function DecisionRow({ item }: { item: DecisionItem }) {
  const { openWorkspaceDrawer } = useAppStore();
  const cfg = PRIORITY_CFG[item.priority];
  const accent = getSportAccent(item.leagueId);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 4 }}
      transition={{ duration: 0.16 }}
      onClick={() => item.rec && openWorkspaceDrawer(item.rec)}
      className={clsx(
        'flex items-start gap-2 px-2.5 py-2 rounded-lg border text-[10px] transition-colors',
        'bg-surface-2 border-surface-4',
        item.rec && 'cursor-pointer hover:border-surface-3 hover:bg-surface-3',
      )}
    >
      <span className={clsx(
        'flex-shrink-0 px-1.5 py-0.5 rounded border text-[8px] font-bold uppercase leading-none mt-0.5',
        cfg.cls,
      )}>
        {item.priority}
      </span>

      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-start justify-between gap-1">
          <p className="font-semibold text-gray-200 leading-snug line-clamp-2 flex-1">
            {item.actionText}
          </p>
          {item.leagueId && (
            <span className={clsx('flex-shrink-0 text-[9px] font-bold', accent.primary)}>
              {accent.label}
            </span>
          )}
        </div>
        {item.rationale && (
          <p className="text-gray-600 leading-snug line-clamp-1">{item.rationale}</p>
        )}
        {item.rec && (
          <div className="flex items-center gap-1 pt-0.5">
            <TeamLogo abbrev={item.rec.awayTeamAbbrev} name={item.rec.awayTeamName} leagueId={item.rec.leagueId} size="xs" />
            <span className="text-[9px] text-gray-700">vs</span>
            <TeamLogo abbrev={item.rec.homeTeamAbbrev} name={item.rec.homeTeamName} leagueId={item.rec.leagueId} size="xs" />
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-0.5">
          {item.countdown !== null && item.countdown !== undefined && (
            <span className={clsx(
              'flex items-center gap-1 text-[9px]',
              item.countdown < 60 ? 'text-warn' : 'text-gray-600',
            )}>
              <Clock className="w-2.5 h-2.5" />
              {formatCountdown(item.countdown)}
            </span>
          )}
          {item.edgeDecay && (
            <DecayBar
              decayPct={item.edgeDecay.decayPct}
              remainingEdge={item.edgeDecay.remainingEdge}
            />
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ── Priority section ──────────────────────────────────────────────────────────

function Section({ priority, items }: { priority: Priority; items: DecisionItem[] }) {
  if (items.length === 0) return null;
  const cfg = PRIORITY_CFG[priority];
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 px-1">
        <div className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', cfg.dotCls)} />
        <span className={clsx('text-[9px] font-bold uppercase tracking-widest', cfg.textCls)}>
          {priority}
        </span>
        <span className="text-[9px] text-gray-700 font-mono">{items.length}</span>
      </div>
      <AnimatePresence initial={false}>
        {items.map((item) => (
          <DecisionRow key={item.id} item={item} />
        ))}
      </AnimatePresence>
    </div>
  );
}

// ── DecisionQueue ─────────────────────────────────────────────────────────────

interface DecisionQueueProps {
  picks: SportRecommendation[];
  signals: IntelligenceSignal[];
  exposure: ExposureReport | null;
  className?: string;
}

export function DecisionQueue({ picks, signals, exposure, className }: DecisionQueueProps) {
  const allItems = useMemo(
    () => deriveDecisions(picks, signals, exposure),
    [picks, signals, exposure],
  );

  const high = allItems
    .filter((i) => i.priority === 'HIGH')
    .sort((a, b) => (a.countdown ?? Infinity) - (b.countdown ?? Infinity));
  const med = allItems.filter((i) => i.priority === 'MED');
  const low = allItems.filter((i) => i.priority === 'LOW');
  const isEmpty = allItems.length === 0;

  return (
    <div className={clsx('flex flex-col h-full', className)}>
      {/* Summary strip */}
      <div className="px-3 py-2 border-b border-surface-4 flex items-center gap-2 flex-shrink-0">
        <div className="flex items-center gap-1.5 flex-1">
          {([['HIGH', high] as const, ['MED', med] as const, ['LOW', low] as const]).map(([p, arr]) => {
            if (arr.length === 0) return null;
            const cfg = PRIORITY_CFG[p];
            return (
              <span key={p} className={clsx('px-1.5 py-0.5 rounded border text-[9px] font-bold', cfg.cls)}>
                {arr.length} {p}
              </span>
            );
          })}
          {isEmpty && (
            <span className="text-[9px] text-gray-600">All clear</span>
          )}
        </div>
        {high.length > 0 && (
          <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-loss opacity-60" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-loss" />
          </span>
        )}
      </div>

      {/* Item list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-3">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-3">
            <CheckCircle className="w-6 h-6 text-gray-700" />
            <p className="text-[10px] text-gray-600 leading-snug">
              All clear — no decisions pending
            </p>
          </div>
        ) : (
          <>
            <Section priority="HIGH" items={high} />
            <Section priority="MED" items={med} />
            <Section priority="LOW" items={low} />
          </>
        )}
      </div>
    </div>
  );
}
